import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  SecurityContext,
  type OnInit,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Message } from 'src/app/core/models/comms.model';
import {
  CommunicationService,
  ThreadWithMessagesAndClaimCase,
} from 'src/app/core/services/communication.service';

@Component({
  selector: 'app-communication-chat',
  templateUrl: './communication-chat.component.html',
  styleUrls: ['./communication-chat.component.scss'],
})
export class CommunicationChatComponent implements OnInit {
  @Input() thread: any;
  @Input() user: any;

  @Input() sendField: 'simple' | 'advanced' = 'simple';
  @Input() breadcrumbType: 'client' | 'firm' = 'client';

  message = '';
  loadingSendMessage = false;

  constructor(private cs: CommunicationService, private sanitizer: DomSanitizer) { }
  ngOnInit(): void { }

  getNewestMessageFromThread(thread: ThreadWithMessagesAndClaimCase) {
    // Sort messages and get the most recent
    return thread.cp_message?.sort((a, b) => {
      return (
        new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
      );
    })[0];
  }

  getSortedMessages(message: Message[]) {
    return message.sort((a, b) => {
      return (
        new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime()
      );
    });
  }

  getTrustedHtml(value: string): SafeHtml | string {
    if (!value) return '';
    
    let cleanedValue = this.cleanEditorHtml(value);
    
    const sanitizedValue = this.sanitizer.sanitize(SecurityContext.HTML, cleanedValue) ?? '';
    
    const linkifiedText = this.linkifyUrls(sanitizedValue);
    
    const finalCleaned = this.sanitizer.sanitize(SecurityContext.HTML, linkifiedText) ?? '';
    return this.sanitizer.bypassSecurityTrustHtml(finalCleaned);
  }

  private cleanEditorHtml(html: string): string {
    return html
      .replace(/<span class="ql-cursor"[^>]*>.*?<\/span>/gi, '') 
      .replace(/\u{FEFF}/gu, '')
      .replace(/<\/p>\s*<p>/gi, '</p><p>')
      .replace(/<p>\s*<\/p>/gi, '')
      .replace(/(<pre[^>]*>)([\s\S]*?)(<\/pre>)/gi, (match, openTag, content, closeTag) => {
        return openTag + content + closeTag;
      })
      .trim();
  }

  private linkifyUrls(html: string): string {
    const urlRegex = /(?<!<a[^>]*>)(?<!href=["'])(?<!href=)\b(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+)\b(?![^<]*<\/a>)/gi;
    
    return html.replace(urlRegex, (url) => {
      if (!this.isValidUrl(url)) {
        return url; 
      }
      
      const href = url.startsWith('www.') ? `https://${url}` : url;
      const safeUrl = url.replace(/"/g, '&quot;');
      const safeHref = href.replace(/"/g, '&quot;');
      
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`;
    });
  }



  private isValidUrl(url: string): boolean {
    try {
      const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
      const urlObj = new URL(fullUrl);
      
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return false;
      }
      
      if (url.toLowerCase().includes('javascript:') || url.toLowerCase().includes('data:')) {
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }

  async closeThread() {
    try {
      const res = await this.cs.closeThread(this.thread.id);
      console.log('Thread closed', res);
    } catch (error) {
      console.error;
    }
  }

  async sendMessage() {
    this.loadingSendMessage = true;
    try {
      const newMessage: Message = {
        value: this.message,
        thread_id: this.thread.id,
        author_user_id: this.user?.id ?? '-',

        type_message: 'INTERNAL_MESSAGE',
        author_display_name: '',
        attachments: {},
        user_agent: {},
        metadata: {},
        user_reads: [],
      };

      const { data } = await this.cs.sendMessage(newMessage);
      console.log('Message saved received', data);
      this.thread.cp_message?.push(data);
      this.message = '';
    } catch (error) {
      console.error(error);
    } finally {
      console.log('Message sent');
      this.loadingSendMessage = false;
    }
  }
}
